import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { gsApiV3 } from "../auth/auth";
import {
  getThunkResponse,
  createThunkResponse,
  saveThunkResponse,
  deleteThunkResponse,
  thunkResponse,
  v3Tournaments,
  v3RecipientGroups,
  v3Recipients,
} from "../app/api";
import { getResponse, longDelay, mediumDelay, shortDelay } from "../helpers/JSONapi";
import { getTournamentPaymentSettings } from "./settingsSlice";

export const getTournamentMessages = createAsyncThunk(
  "messages/getTournamentMessages",
  async (tournamentID, thunkAPI) => {
    const response = getResponse(
      gsApiV3.get,
      `${v3Tournaments}/${tournamentID}/messages`,
      undefined,
      thunkAPI
    );
    return response;
  }
);

export const getMessage = createAsyncThunk(
  "messages/getMessage",
  async ({ tournamentID, messageID, include, recipientStats }, thunkAPI) => {
    const params = {};
    if (include) params.include = include;
    if (recipientStats) params.recipientStats = true;
    const response = getResponse(
      gsApiV3.get,
      `${v3Tournaments}/${tournamentID}/messages/${messageID}`,
      Object.keys(params).length ? { data: params } : undefined,
      thunkAPI
    );
    return response;
  }
);

export const refreshMessageRecipients = createAsyncThunk(
  "messages/refreshMessageRecipients",
  async ({ tournamentID, messageID, recipientGroupId }, thunkAPI) => {
    const response = getResponse(
      gsApiV3.post,
      `${v3Tournaments}/${tournamentID}/messages/${messageID}/refresh-recipients`,
      recipientGroupId ? { recipient_group_id: recipientGroupId } : {},
      thunkAPI
    );
    return response;
  }
);

export const refreshAllRecipients = createAsyncThunk(
  "messages/refreshAllRecipients",
  async ({ tournamentID, messageID, manualRefresh }, thunkAPI) => {
    const response = getResponse(
      gsApiV3.post,
      `${v3Tournaments}/${tournamentID}/messages/${messageID}/refresh-all-recipients`,
      manualRefresh ? { manualRefresh: true } : {},
      thunkAPI
    );
    return response;
  }
);

export const createMessage = createAsyncThunk(
  "messages/createMessage",
  async ({ tournamentID, data }, thunkAPI) => {
    const response = getResponse(
      gsApiV3.post,
      `${v3Tournaments}/${tournamentID}/messages`,
      data,
      thunkAPI
    );
    return response;
  }
);

export const updateMessage = createAsyncThunk(
  "messages/updateMessage",
  async ({ tournamentID, messageID, data }, thunkAPI) => {
    const response = getResponse(
      gsApiV3.put,
      `${v3Tournaments}/${tournamentID}/messages/${messageID}`,
      data,
      thunkAPI
    );
    return response;
  }
);

export const patchMessage = createAsyncThunk(
  "messages/patchMessage",
  async ({ tournamentID, messageID, data }, thunkAPI) => {
    const response = getResponse(
      gsApiV3.patch,
      `${v3Tournaments}/${tournamentID}/messages/${messageID}`,
      data,
      thunkAPI
    );
    return response;
  }
);

export const deleteMessage = createAsyncThunk(
  "messages/deleteMessage",
  async ({ tournamentID, messageID }, thunkAPI) => {
    const response = getResponse(
      gsApiV3.delete,
      `${v3Tournaments}/${tournamentID}/messages/${messageID}`,
      { data: {} },
      thunkAPI
    );
    return response;
  }
);

export const cancelMessage = createAsyncThunk(
  "messages/cancelMessage",
  async ({ tournamentID, messageID }, thunkAPI) => {
    const response = getResponse(
      gsApiV3.post,
      `${v3Tournaments}/${tournamentID}/messages/${messageID}/cancel`,
      {},
      thunkAPI
    );
    return response;
  }
);

export const sendMessage = createAsyncThunk(
  "messages/sendMessage",
  async ({ tournamentID, messageID }, thunkAPI) => {
    const response = getResponse(
      gsApiV3.post,
      `${v3Tournaments}/${tournamentID}/messages/${messageID}/send`,
      {},
      thunkAPI
    );
    return response;
  }
);

export const createRecipientGroup = createAsyncThunk(
  "messages/createRecipientGroup",
  async ({ messageID, data }, thunkAPI) => {
    const response = getResponse(
      gsApiV3.post,
      v3RecipientGroups,
      { ...data, message: { id: messageID } },
      thunkAPI
    );
    return response;
  }
);

export const updateRecipientGroup = createAsyncThunk(
  "messages/updateRecipientGroup",
  async ({ groupID, data }, thunkAPI) => {
    const response = getResponse(
      gsApiV3.patch,
      `${v3RecipientGroups}/${groupID}`,
      data,
      thunkAPI
    );
    return response;
  }
);

export const updateRecipient = createAsyncThunk(
  "messages/updateRecipient",
  async ({ recipientID, data }, thunkAPI) => {
    const response = getResponse(
      gsApiV3.patch,
      `${v3Recipients}/${recipientID}`,
      data,
      thunkAPI
    );
    return response;
  }
);

export const getRecipientGroup = createAsyncThunk(
  "messages/getRecipientGroup",
  async ({ groupID, page, perPage, q, selected, status }, thunkAPI) => {
    const params = {};
    if (page || perPage) {
      params.page = {};
      if (page) params.page.number = page;
      if (perPage) params.page.size = perPage;
    }
    if (q) params.q = q;
    if (selected !== undefined) params.selected = selected;
    if (status) params.status = status;
    const response = getResponse(
      gsApiV3.get,
      `${v3RecipientGroups}/${groupID}`,
      { data: params },
      thunkAPI
    );
    return response;
  }
);

export const deleteRecipientGroup = createAsyncThunk(
  "messages/deleteRecipientGroup",
  async ({ groupID }, thunkAPI) => {
    const response = getResponse(
      gsApiV3.delete,
      `${v3RecipientGroups}/${groupID}`,
      { data: {} },
      thunkAPI
    );
    return response;
  }
);

const resolveSelectedCount = (group) => {
  if (Array.isArray(group?.recipients) && group.recipients.length > 0) {
    return group.recipients.filter((r) => r?.selected === true).length;
  }
  return group?.selectedCount ?? 0;
};

const syncMessageGroupCounts = (state) => {
  const message = state.messages.find(
    (m) => m.id === state.currentMessage?.id
  );
  if (!message) return;
  message.selectedGroupsCount = (
    state.currentMessage?.recipientGroups ?? []
  ).filter((g) => g?.includeAll === true || (g?.selectedCount ?? 0) > 0).length;
};

export const messagesSlice = createSlice({
  name: "messages",
  initialState: {
    messages: [],
    currentMessage: {},
    currentRecipientGroup: {},
    tournamentMessagingSettings: {},
    notifications: [],
    loading: [],
  },
  reducers: {
    setCurrentMessage: (state, action) => {
      state.currentMessage = action.payload;
    },
    clearNotifications: (state) => {
      state.notifications = [];
    },
    clearCurrentMessage: (state) => {
      state.currentMessage = {};
    },
    clearCurrentRecipientGroup: (state) => {
      state.currentRecipientGroup = {};
    },
  },
  extraReducers: {
    ...getThunkResponse(getTournamentMessages, (state, action) => {
      state.messages = action.payload ?? [];
    }),
    ...getThunkResponse(getMessage, (state, action) => {
      state.currentMessage = action.payload ?? {};
    }),
    ...getThunkResponse(refreshMessageRecipients, (state, action) => {
      state.currentMessage = action.payload ?? {};
      state.messages = state.messages.map((m) =>
        m.id === state.currentMessage.id ? state.currentMessage : m
      );
    }),
    ...getThunkResponse(refreshAllRecipients, (state, action) => {
      state.currentMessage = action.payload ?? {};
      state.messages = state.messages.map((m) =>
        m.id === state.currentMessage.id ? state.currentMessage : m
      );
    }),
    ...createThunkResponse(
      createMessage,
      (state, action) => {
        state.currentMessage = action.payload;
        state.messages.push(action.payload);
      },
      "Message"
    ),
    ...saveThunkResponse(
      updateMessage,
      (state, action) => {
        state.currentMessage = action.payload;
        state.messages = state.messages.map((m) =>
          m.id === action.payload.id ? action.payload : m
        );
      },
      "Message"
    ),
    ...saveThunkResponse(
      patchMessage,
      (state, action) => {
        state.currentMessage = action.payload;
        state.messages = state.messages.map((m) =>
          m.id === action.payload.id ? action.payload : m
        );
      },
      "Message"
    ),
    ...deleteThunkResponse(
      deleteMessage,
      (state, action) => {
        state.messages = state.messages.filter(
          (m) => m.id !== action.meta.arg.messageID
        );
        state.currentMessage = {};
      },
      "Message"
    ),
    ...thunkResponse(
      cancelMessage,
      (state, action) => {
        state.currentMessage = action.payload;
        state.messages = state.messages.map((m) =>
          m.id === action.payload.id ? action.payload : m
        );
      },
      "Message",
      {
        pendingText: "Canceling Scheduled Send...",
        pendingDelay: shortDelay,
        fulfilledText: "Scheduled send has been canceled.",
        fulfilledDelay: mediumDelay,
        rejectedDelay: longDelay,
      }
    ),
    ...thunkResponse(
      sendMessage,
      (state, action) => {
        state.currentMessage = action.payload;
        state.messages = state.messages.map((m) =>
          m.id === action.payload.id ? action.payload : m
        );
      },
      "Message",
      {
        pendingText: "Sending Message...",
        pendingDelay: shortDelay,
        fulfilledText: "Message has been sent!",
        fulfilledDelay: mediumDelay,
        rejectedDelay: longDelay,
      }
    ),
    ...createThunkResponse(
      createRecipientGroup,
      (state, action) => {
        const group = {
          ...action.payload,
          tournamentId:
            action.payload?.tournamentId ??
            action.meta.arg.data?.tournament?.id ??
            action.meta.arg.tournamentID,
        };
        group.selectedCount = resolveSelectedCount(group);
        const isExclusion =
          action.payload?.exclusion === true ||
          action.meta.arg.data?.exclusion === true;
        if (isExclusion) {
          state.currentMessage.exclusionGroups = [
            ...(state.currentMessage?.exclusionGroups ?? []),
            group,
          ];
        } else {
          state.currentMessage.recipientGroups = [
            ...(state.currentMessage?.recipientGroups ?? []),
            group,
          ];
        }
        const updatedGroups = action.payload?.updatedGroups ?? [];
        if (updatedGroups.length > 0) {
          const updatedById = new Map(updatedGroups.map((g) => [g.id, g]));
          state.currentMessage.recipientGroups = (
            state.currentMessage?.recipientGroups ?? []
          ).map((g) => {
            const updated = updatedById.get(g.id);
            if (!updated) return g;
            const merged = { ...g, ...updated, tournamentId: g.tournamentId };
            merged.selectedCount = resolveSelectedCount(merged);
            return merged;
          });
        }
        syncMessageGroupCounts(state);
      },
      "Recipient Group"
    ),
    ...saveThunkResponse(
      updateRecipientGroup,
      (state, action) => {
        const groups = state.currentMessage?.recipientGroups ?? [];
        state.currentMessage.recipientGroups = groups.map((g) => {
          if (g.id !== action.payload.id) return g;
          const merged = { ...g, ...action.payload, tournamentId: g.tournamentId };
          merged.selectedCount = resolveSelectedCount(merged);
          return merged;
        });
        if (state.currentRecipientGroup?.id === action.payload.id) {
          state.currentRecipientGroup = {
            ...state.currentRecipientGroup,
            ...action.payload,
          };
        }
        syncMessageGroupCounts(state);
      },
      "Recipient Group"
    ),
    ...getThunkResponse(updateRecipient, (state, action) => {
      const updated = action.payload;
      const group = state.currentRecipientGroup;
      const recipient = group?.recipients?.find((r) => r.id === updated.id);
      if (!recipient) return;

      const wasSelected = recipient.selected === true;
      Object.assign(recipient, updated);
      const isSelected = recipient.selected === true;
      if (wasSelected === isSelected) return;

      const delta = isSelected ? 1 : -1;
      group.selectedCount = Math.max(0, (group.selectedCount ?? 0) + delta);

      const messageGroup = state.currentMessage?.recipientGroups?.find(
        (g) => g.id === group.id
      );
      if (messageGroup) {
        messageGroup.selectedCount = Math.max(
          0,
          (messageGroup.selectedCount ?? 0) + delta
        );
      }
      syncMessageGroupCounts(state);
    }),
    ...getThunkResponse(getTournamentPaymentSettings, (state, action) => {
      const tournamentID = action.meta?.arg;
      if (!tournamentID) return;
      state.tournamentMessagingSettings[tournamentID] = {
        donationsActive: action.payload?.donations === true,
      };
    }),
    ...getThunkResponse(getRecipientGroup, (state, action) => {
      const payload = action.payload ?? {};
      const requestedPage = action.meta?.arg?.page ?? 1;
      const previous = state.currentRecipientGroup ?? {};
      const incoming = payload.recipients ?? [];
      state.currentRecipientGroup = {
        ...previous,
        ...payload,
        recipients:
          requestedPage > 1
            ? [...(previous.recipients ?? []), ...incoming]
            : incoming,
      };
    }),
    ...deleteThunkResponse(
      deleteRecipientGroup,
      (state, action) => {
        const groupID = action.meta.arg.groupID;
        state.currentMessage.recipientGroups = (
          state.currentMessage?.recipientGroups ?? []
        ).filter((g) => g.id !== groupID);
        state.currentMessage.exclusionGroups = (
          state.currentMessage?.exclusionGroups ?? []
        ).filter((g) => g.id !== groupID);
        syncMessageGroupCounts(state);
      },
      "Recipient Group"
    ),
  },
});

export const { setCurrentMessage, clearNotifications, clearCurrentMessage, clearCurrentRecipientGroup } = messagesSlice.actions;

export const selectMessages = (state) =>
  state?.[messagesSlice.name]?.messages;
export const selectCurrentMessage = (state) =>
  state?.[messagesSlice.name]?.currentMessage;
export const selectCurrentMessageRecipientGroups = (state) =>
  state?.[messagesSlice.name]?.currentMessage?.recipientGroups ?? [];
export const selectCurrentMessageExclusionGroups = (state) =>
  state?.[messagesSlice.name]?.currentMessage?.exclusionGroups ?? [];
export const selectCurrentRecipientGroup = (state) =>
  state?.[messagesSlice.name]?.currentRecipientGroup;
export const selectMessagesNotifications = (state) =>
  state?.[messagesSlice.name]?.notifications;
export const selectMessagesLoading = (state) =>
  state?.[messagesSlice.name]?.loading;
export const selectTournamentMessagingSettings = (state, tournamentID) =>
  state?.[messagesSlice.name]?.tournamentMessagingSettings?.[tournamentID];

export const selectCurrentMessageTournaments = (state) =>
  state?.[messagesSlice.name]?.currentMessage?.tournaments ?? [];

export const selectCurrentMessageRecipientCount = (state) =>
  state?.[messagesSlice.name]?.currentMessage?.uniqueSelectedCount ?? 0;

export const selectSelectedMessageTournaments = (
  state,
  ownerTournamentID,
  ownerTournament
) => {
  const messageTournaments = selectCurrentMessageTournaments(state);
  const ownerFromMessage = messageTournaments.find(
    (t) => t?.id === ownerTournamentID
  );
  const owner = ownerFromMessage
    ? { ...(ownerTournament ?? {}), ...ownerFromMessage }
    : ownerTournament?.id
      ? ownerTournament
      : { id: ownerTournamentID };
  const additional = messageTournaments.filter(
    (t) => t?.id && t.id !== ownerTournamentID
  );
  return [owner, ...additional];
};

export default messagesSlice.reducer;
